import { Dispatch, ForwardedRef, forwardRef, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import FilterIcon from 'assets/filterIcon.svg';
import { Button, SelectionPanel, Tooltip, Visible } from 'coreOld/components';
import { Icon } from 'coreOld/elements';
import { FilterField as FilterFieldType, Size } from 'coreOld/enums';
import { removeNullValues } from 'coreOld/utils';

import {
  ButtonsWrapper,
  ButtonWrapper,
  Chip,
  ChipButton,
  ClearIcon,
  Container,
  FilterItem,
  FilterLabel,
  FilterWrapper,
  IconImg,
  InputsWrapper,
  Margin,
  panelStyle,
} from './advancedFilterStyle';
import FilterField from '../FilterField';

interface IProps {
  values?: Record<string, any>;
  customPanelStyle?;
  advancedFiltersConfig?;
  advancedFiltersValues?: Record<string, any>;
  onChangeAdvancedFilter: (name: string, value: number | string, clearFilters?: boolean) => void;
  onApplyAdvancedFilters: () => void;
  onClearAdvancedFilters: (name: string, value: number | string, clearFilters?: boolean) => void;
  appliedAdvancedFilters?: number;
  setAppliedAdvancedFilters?: Dispatch<SetStateAction<number>>;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
}

const AdvancedFilterView = forwardRef<HTMLElement, IProps>(function AdvanceFilterView(
  {
    values,
    customPanelStyle,
    advancedFiltersConfig,
    advancedFiltersValues,
    onChangeAdvancedFilter,
    onApplyAdvancedFilters,
    onClearAdvancedFilters,
    appliedAdvancedFilters,
    setAppliedAdvancedFilters,
    isModalOpen,
    setIsModalOpen,
  }: IProps,
  ref: ForwardedRef<HTMLElement>
) {
  const { t } = useTranslation();

  const advancedFiltersNumber = Object.keys(removeNullValues(advancedFiltersValues)).length;

  return (
    <Container ref={ref}>
      <ButtonsWrapper>
        <Visible when={appliedAdvancedFilters > 0}>
          <Chip>{appliedAdvancedFilters}</Chip>
          <ChipButton onClick={() => onClearAdvancedFilters(undefined, undefined, true)}>
            {t('buttonLabelClear')}
          </ChipButton>
        </Visible>

        <Tooltip text={t('labelAdvancedFilter')}>
          <IconImg src={FilterIcon} onClick={() => setIsModalOpen((prev) => !prev)} />
        </Tooltip>
      </ButtonsWrapper>

      <SelectionPanel
        title={t('labelAdvancedFilter')}
        fontSize={Size.SM}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        style={panelStyle(customPanelStyle)}
      >
        <InputsWrapper>
          {advancedFiltersConfig?.map((item) => (
            <FilterWrapper key={item.name} minWidth={item?.minWidth}>
              <Visible when={Boolean(item?.label)}>
                <FilterLabel>{item.label}</FilterLabel>
              </Visible>

              <FilterItem>
                <FilterField
                  values={values}
                  filter={item}
                  onChangeFilter={onChangeAdvancedFilter}
                />
              </FilterItem>

              <Visible when={FilterFieldType.INPUT_GROUP !== item.type || !item?.individualFields}>
                <ClearIcon
                  isHidden={!values[item.name] || !values?.[item.name].length}
                  onClick={() => onChangeAdvancedFilter(item.name, null)}
                >
                  <Icon name={Icon.CLEAR} />
                </ClearIcon>
              </Visible>
            </FilterWrapper>
          ))}

          <ButtonWrapper>
            <Visible when={advancedFiltersNumber > 0}>
              <Margin>
                <Button onClick={() => onClearAdvancedFilters(undefined, undefined, true)}>
                  {t('buttonLabelClear')}
                </Button>
              </Margin>
            </Visible>

            <Button
              disabled={!advancedFiltersNumber && !appliedAdvancedFilters}
              onClick={() => {
                onApplyAdvancedFilters();
                setIsModalOpen(false);
                setAppliedAdvancedFilters(advancedFiltersNumber);
              }}
            >
              {t('buttonLabelApply')}
            </Button>
          </ButtonWrapper>
        </InputsWrapper>
      </SelectionPanel>
    </Container>
  );
});

export default AdvancedFilterView;
